import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, ChevronLeft, Loader2, Plus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { formatDay } from "../lib/finance";
import { OBLIGATION_VIEWS, RECURRENCES, canWrite, dueText, recordsError } from "../lib/records";
import {
  CancelDialog, DoneDialog, ObligationActions, ObligationBadge, ObligationDialog, ObligationLine, useObligationTask,
} from "../components/records/ObligationBits";
import { Field } from "../components/records/RecordBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";

const PAGE_SIZE = 50;
const HISTORY = { created: "Créée", updated: "Modifiée", done: "Faite", cancelled: "Annulée", task_linked: "Tâche reliée" };

function ObligationSheet({ id, onClose, onChanged, onAction }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!id) return;
    setDetail(null);
    setError("");
    api.get(`/records/obligations/${id}`).then(({ data }) => setDetail(data)).catch((e) => setError(recordsError(e, "Obligation introuvable.")));
  }, [id, onChanged]);
  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="obligation-sheet">
        <SheetHeader>
          <SheetTitle>{detail?.title || "Obligation"}</SheetTitle>
          <SheetDescription>{detail ? `${detail.kind_label} · ${dueText(detail)}` : " "}</SheetDescription>
        </SheetHeader>
        {error && <p className="mt-4 text-sm text-red-600" role="alert">{error}</p>}
        {!detail && !error && <div className="mt-4 space-y-2"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-6 w-1/2" /></div>}
        {detail && (
          <div className="mt-4 space-y-5">
            <div className="flex items-center justify-between gap-2">
              <ObligationBadge state={detail.state} />
              <ObligationActions obligation={detail} onEdit={(o) => onAction("edit", o)} onDone={(o) => onAction("done", o)}
                onCancel={(o) => onAction("cancel", o)} onTask={(o) => onAction("task", o)} onDelete={(o) => onAction("delete", o)} />
            </div>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Date limite">{formatDay(detail.due_date)}</Field>
              <Field label="Récurrence">{RECURRENCES[detail.recurrence]}</Field>
              <Field label="Responsable">{detail.responsible_name}</Field>
              <Field label="Document">{detail.document ? <Link to={`/documents/${detail.document.id}`} className="text-jamora hover:underline">{detail.document.number} · {detail.document.title}</Link> : "Aucun"}</Field>
            </dl>
            {detail.note && <p className="text-sm text-slate-700 whitespace-pre-line">{detail.note}</p>}
            {detail.cancel_reason && <p className="text-sm text-slate-600">Motif d'annulation : {detail.cancel_reason}</p>}
            {detail.state === "done" && (
              <p className="text-sm text-slate-600">Faite le {formatDay(detail.done_on)}{detail.done_by_name ? ` par ${detail.done_by_name}` : ""}{detail.done_note ? ` — ${detail.done_note}` : ""}</p>
            )}
            {(detail.previous_id || detail.next_id) && (
              <div className="flex flex-wrap gap-3 text-sm">
                {detail.previous_id && <Link to={`/documents/obligations?obligation=${detail.previous_id}`} className="text-jamora hover:underline">Échéance précédente</Link>}
                {detail.next_id && <Link to={`/documents/obligations?obligation=${detail.next_id}`} className="text-jamora hover:underline" data-testid="obligation-next">Échéance suivante</Link>}
              </div>
            )}
            <section className="space-y-1.5">
              <h3 className="text-sm font-semibold text-slate-900">Tâches</h3>
              {(detail.tasks || []).length === 0 && <p className="text-sm text-slate-500">Aucune tâche reliée.</p>}
              <ul className="space-y-1" data-testid="obligation-tasks">
                {(detail.tasks || []).map((t) => (
                  <li key={t.id} className="text-sm">
                    {t.visible ? <Link to="/tasks" state={{ openTask: t.id }} className="text-jamora hover:underline">{t.title}</Link> : <span className="text-slate-500">Une tâche que vous ne voyez pas</span>}
                  </li>
                ))}
              </ul>
            </section>
            <section className="space-y-1.5">
              <h3 className="text-sm font-semibold text-slate-900">Historique</h3>
              <ul className="space-y-1 text-sm text-slate-600">
                {(detail.history || []).map((h) => (
                  <li key={h.id}>{HISTORY[h.kind] || h.kind} · {h.actor_name} · {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function RecordObligations() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = OBLIGATION_VIEWS.some((v) => v.id === params.get("vue")) ? params.get("vue") : "open";
  const search = params.get("q") || "";
  const openId = params.get("obligation");
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [revision, setRevision] = useState(0);
  const seq = useRef(0);

  const setParam = useCallback((name, value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(name, value); else next.delete(name);
    return next;
  }, { replace: name !== "obligation" }), [setParams]);

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput.trim() !== search) setParam("q", searchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE, ...OBLIGATION_VIEWS.find((v) => v.id === view).params };
    if (search) out.search = search;
    return out;
  }, [view, search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? "ready" : "loading"));
    try {
      const { data: body } = await api.get("/records/obligations", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(recordsError(e, "Impossible de charger les obligations."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const refresh = useCallback(() => { setRevision((r) => r + 1); load(); }, [load]);
  const task = useObligationTask(refresh);
  const onAction = (kind, obligation) => (kind === "task" ? task.open?.(obligation) : setDialog({ kind, obligation }));
  const done = () => { setDialog(null); refresh(); };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/records/obligations", { params: { ...query, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(recordsError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="obligations-page">
      <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Documents et contrats</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Obligations</h1>
          <p className="text-sm text-slate-500">Ce qu'il faut faire avant une date : préavis, renouvellements, déclarations, cotisations. Le responsable est prévenu à l'avance.</p>
        </div>
        {canWrite(user, "records:create") && (
          <Button onClick={() => setDialog({ kind: "create" })} data-testid="obligations-new"><Plus size={16} className="mr-1.5" />Nouvelle obligation</Button>
        )}
      </div>

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Vues des obligations">
        {OBLIGATION_VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={view === v.id} onClick={() => setParam("vue", v.id === "open" ? "" : v.id)}
            data-testid={`obligations-view-${v.id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${view === v.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Rechercher une obligation…" className="pl-9 h-9"
            aria-label="Rechercher une obligation" data-testid="obligations-search" />
        </div>
        {search && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={() => setSearchInput("")}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="obligations-empty">
          <CalendarClock className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{search || view !== "open" ? "Aucune obligation ne correspond" : "Aucune obligation à venir"}</h3>
          {!search && view === "open" && (
            <p className="text-sm text-slate-500 max-w-md mx-auto">Ajoutez un préavis ou un renouvellement depuis un contrat, ou une obligation libre (déclaration, cotisation) ici.</p>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <Card className="px-4 divide-y divide-slate-100" data-testid="obligations-list">
          {data.items.map((o) => (
            <ObligationLine key={o.id} obligation={o}>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setParam("obligation", o.id)} data-testid={`obligation-open-${o.id}`}>Voir</Button>
                <ObligationActions obligation={o} onEdit={(ob) => onAction("edit", ob)} onDone={(ob) => onAction("done", ob)}
                  onCancel={(ob) => onAction("cancel", ob)} onTask={task.open ? (ob) => onAction("task", ob) : null} onDelete={(ob) => onAction("delete", ob)} />
              </div>
            </ObligationLine>
          ))}
        </Card>
      )}
      {phase === "ready" && data.items.length < data.total && (
        <div className="text-center">
          <Button variant="outline" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
        </div>
      )}

      <ObligationSheet id={openId} onClose={() => setParam("obligation", "")} onChanged={revision} onAction={onAction} />
      <ObligationDialog open={dialog?.kind === "create" || dialog?.kind === "edit"} onClose={() => setDialog(null)} onDone={done}
        obligation={dialog?.kind === "edit" ? dialog.obligation : null} document={dialog?.obligation?.document} />
      <DoneDialog obligation={dialog?.obligation} open={dialog?.kind === "done"} onClose={() => setDialog(null)} onDone={done} />
      <DeleteWithReasonDialog
        target={dialog?.kind === "delete" ? dialog.obligation : null} testId="delete-obligation" onClose={() => setDialog(null)}
        title={`Supprimer l'obligation « ${dialog?.obligation?.title} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une obligation faite, ou avec des tâches liées, se marque faite ou s'annule à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/records/obligations/${dialog.obligation.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            setParam("obligation", "");
            done();
          } catch (e) {
            toast.error(recordsError(e, "Impossible de supprimer cette obligation."));
            setDialog(null);
          }
        }}
      />
      <CancelDialog obligation={dialog?.obligation} open={dialog?.kind === "cancel"} onClose={() => setDialog(null)} onDone={done} />
      {task.sheet}
    </div>
  );
}
